import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PhoneCall, Clock, Mic } from "lucide-react";
import RecordingButton from "@/components/RecordingButton";

export default function OwnerCallsEmails() {
  const { lang } = useI18n();
  const L = lang === "el";
  const [reps, setReps] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => {
      const onlyReps = (r.data || []).filter((u) => u.role === "sales_rep");
      setReps(onlyReps);
      if (onlyReps.length && !selectedId) setSelectedId(onlyReps[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) { setCalls([]); return; }
    setLoading(true);
    api.get(`/rep/${selectedId}/calls`)
      .then((r) => setCalls(r.data || []))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const outcomeLabel = (o) => {
    const labels = {
      no_answer: L ? "Δεν απάντησε" : "No answer",
      callback_requested: L ? "Ζήτησε κλήση" : "Callback",
      interested: L ? "Ενδιαφερόμενος" : "Interested",
      not_interested: L ? "Δεν ενδιαφέρεται" : "Not interested",
      meeting_scheduled: L ? "Συνάντηση" : "Meeting",
      proposal_requested: L ? "Πρόταση" : "Proposal",
      deal_won: L ? "Κερδισμένη" : "Won",
      deal_lost: L ? "Χαμένη" : "Lost",
    };
    return labels[o] || o;
  };

  return (
    <div className="space-y-6" data-testid="owner-calls-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Δραστηριότητα" : "Activity"}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {L ? "Κλήσεις πωλητών" : "Rep Calls"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {L ? "Δείτε όλες τις κλήσεις του επιλεγμένου πωλητή" : "Review every call made by the selected rep"}
          </p>
        </div>
        <div className="min-w-[260px]">
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Πωλητής" : "Sales rep"}</label>
          <select
            data-testid="owner-calls-rep-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm bg-white"
          >
            <option value="">{L ? "— Επιλέξτε —" : "— Select —"}</option>
            {reps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-[#0052CC] border-b-2 border-[#0052CC] -mb-3">
          <PhoneCall className="w-4 h-4" /> {L ? "Κλήσεις" : "Calls"} ({calls.length})
        </div>
        {selectedId && (
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-semibold" data-testid="owner-recordings-count">
              <Mic className="w-3.5 h-3.5" /> {calls.filter((c) => c.recording_url).length} {L ? "ηχογραφήσεις" : "recordings"}
            </span>
            <span>· {calls.reduce((s, c) => s + (c.duration_minutes || 0), 0)} {L ? "συν. λεπτά" : "min total"}</span>
          </div>
        )}
      </div>

      {!selectedId ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
          {L ? "Επιλέξτε πωλητή" : "Pick a rep"}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">{L ? "Φόρτωση..." : "Loading..."}</div>
          ) : calls.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <PhoneCall className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <div className="text-sm">{L ? "Καμία κλήση ακόμη" : "No calls yet"}</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="owner-calls-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {[L ? "Ημ/νία" : "Date", L ? "Αριθμός" : "Number", L ? "Διάρκεια" : "Duration", L ? "Αποτέλεσμα" : "Outcome", L ? "Ηχογράφηση" : "Recording", L ? "Σημειώσεις" : "Notes"].map((h) => (
                      <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap text-xs">
                        {new Date(c.date).toLocaleString(L ? "el-GR" : "en-GB")}
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-900">{c.phone_number_called || "—"}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Clock className="w-3.5 h-3.5 text-slate-400" /> {c.duration_minutes}m
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {outcomeLabel(c.outcome)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {c.recording_url ? (
                          <RecordingButton callId={c.id} testid={`owner-recording-${c.id}`} />
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs max-w-md truncate">{c.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
